/**
 * PddManagement.js — Admin only
 * List of all PDD records submitted by users
 * Admin can Approve (Yes) or Reject (No) each PDD
 * On Approve → lead moves to Revenue stage
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiCheckSquare, FiCheck, FiX, FiPhone,
  FiCalendar, FiRefreshCw, FiClock, FiSearch,
  FiEye, FiAlertCircle, FiTrendingUp,
} from 'react-icons/fi';
import * as api from '@crm/shared/api';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';

/* ─── Colors ─── */
const C = {
  navy:'#0D3B5E', navyL:'#EBF4FF',
  gold:'#F5A623', goldL:'#FEF3DC',
  green:'#16A34A', greenL:'#DCFCE7', greenB:'#86EFAC',
  red:'#DC2626',  redL:'#FEF2F2',  redB:'#FECACA',
  teal:'#0D9488', tealL:'#CCFBF1', tealB:'#5EEAD4',
  border:'#E5E7EB', borderL:'#F3F4F6', bg:'#F4F6F9',
  white:'#FFFFFF', t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

const fmtDate = (s) => {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
  } catch { return s; }
};
const fmtAmt = n => n ? '₹' + Number(n).toLocaleString('en-IN') : '—';

/* ─── Shimmer ─── */
const Sh = () => (
  <div style={{ height:58, borderRadius:8, background:'#F1F5F9', marginBottom:8,
    animation:'cfShimmer 1.4s infinite',
    backgroundImage:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)',
    backgroundSize:'400px 100%' }}/>
);
const Spin = () => (
  <div style={{ width:13, height:13, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.35)',
    borderTopColor:'#fff', animation:'cfSpin 0.7s linear infinite', flexShrink:0 }}/>
);

/* ─── Status config ─── */
const S_CFG = {
  PENDING:  { label:'Pending',  color:C.gold,  bg:C.goldL,  border:'#FDE68A' },
  CLEARED:  { label:'Approved', color:C.green, bg:C.greenL, border:C.greenB  },
  REJECTED: { label:'Rejected', color:C.red,   bg:C.redL,   border:C.redB    },
};

/* ════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════ */
export default function PddManagement() {
  const navigate = useNavigate();
  const [records,   setRecords]   = useState([]);
  const [leadsMap,  setLeadsMap]  = useState({});
  const [loading,   setLoading]   = useState(true);
  const [actioning, setActioning] = useState(null); // pdd id being actioned
  const [filter,    setFilter]    = useState('PENDING');
  const [search,    setSearch]    = useState('');
  const [page,      setPage]      = useState(1);
  const [total,     setTotal]     = useState(0);
  const PAGE = 20;

  /* ── Fetch PDD list ── */
  const fetchData = useCallback(async (pg = 1, status = 'PENDING', q = '') => {
    setLoading(true);
    try {
      const params = { page: pg, page_size: PAGE };
      if (status !== 'ALL') params.status = status;
      if (q.trim()) params.search = q.trim();

      const res = await api.getLeadPdds(params);
      const data = res?.data;
      const arr  = Array.isArray(data?.results) ? data.results
                 : Array.isArray(data)           ? data : [];
      setRecords(arr);
      setTotal(data?.count || arr.length);
      setPage(pg);

      // No need to fetch lead details separately - it's included in lead_detail
      const map = {};
      arr.forEach(p => {
        if (p.lead_detail) map[p.lead] = p.lead_detail;
      });
      setLeadsMap(prev => ({ ...prev, ...map }));
    } catch (e) {
      toast.error('Failed to load PDD records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(1, filter, search); }, []); // eslint-disable-line

  /* ── Approve ── */
  const handleApprove = async (pdd) => {
    if (!window.confirm(`Approve PDD for lead "${leadsMap[pdd.lead]?.customer_name || '#'+pdd.lead}"?`)) return;
    const invoiceRequired = window.confirm('Is invoice required for this case?\n\nOK = Yes, invoice required\nCancel = No, go directly to Revenue Management');
    setActioning(pdd.id);
    try {
      await api.patchLeadPdd(pdd.id, { status: 'CLEARED', pdd_admin_approved: true });
      
      const leadId = pdd.lead;
      try {
        const revRes = await api.getLeadRevenueByLead(leadId);
        const revRows = Array.isArray(revRes?.data?.results) ? revRes.data.results : Array.isArray(revRes?.data) ? revRes.data : revRes?.data?.id ? [revRes.data] : [];
        const revData = revRows.find(r => String(r.lead) === String(leadId)) || null;
        const revenuePayload = {
          lead: leadId,
          invoice_required: invoiceRequired,
          payout_to_company: Number(revData?.payout_to_company || 0),
          revenue_received: Number(revData?.revenue_received || 0),
          received_amount: revData?.received_amount || null,
          date_of_receipt: revData?.date_of_receipt || null,
          mode_of_receipt: revData?.mode_of_receipt || null,
        };
        if (revData?.id) {
          await api.patchLeadRevenue(revData.id, revenuePayload);
        } else {
          await api.createLeadRevenue(revenuePayload);
        }
      } catch (revErr) {
        await api.createLeadRevenue({
          lead: leadId,
          invoice_required: invoiceRequired,
          payout_to_company: 0,
          revenue_received: 0,
        });
      }
      toast.success(`PDD Approved! ${invoiceRequired ? 'Invoice required.' : 'Go to Revenue Management.'}`);
      navigate(invoiceRequired ? '/invoice-management' : '/revenue-management');
    } catch (e) {
      toast.error(e?.message || 'Failed to approve');
    } finally { setActioning(null); }
  };

  /* ── Reject ── */
  const handleReject = async (pdd) => {
    const reason = window.prompt('Rejection reason (required):');
    if (!reason) { toast.error('Rejection reason is required'); return; }
    setActioning(pdd.id);
    try {
      await api.patchLeadPdd(pdd.id, { status: 'REJECTED', rejection_reason: reason, pdd_admin_approved: false });
      toast.success('PDD Rejected — lead returned to Disbursement');
      fetchData(page, filter, search);
    } catch (e) {
      toast.error(e?.message || 'Failed to reject');
    } finally { setActioning(null); }
  };

  /* ── Filter tab click ── */
  const onTabClick = (val) => {
    setFilter(val);
    fetchData(1, val, search);
  };

  const totalPages = Math.ceil(total / PAGE);

  /* ─── Status badge ─── */
  const StatusBadge = ({ pdd }) => {
    const s = pdd.status === 'CLEARED' || pdd.pdd_admin_approved === true ? 'CLEARED'
            : pdd.status === 'REJECTED' ? 'REJECTED'
            : 'PENDING';
    const cfg = S_CFG[s] || S_CFG.PENDING;
    return (
      <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:20,
        fontSize:11.5, fontWeight:700, background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.border}` }}>
        {s === 'PENDING'  && <FiClock size={10}/>}
        {s === 'CLEARED' && <FiCheck size={10}/>}
        {s === 'REJECTED' && <FiX size={10}/>}
        {cfg.label}
      </span>
    );
  };

  const isPending = (pdd) => pdd.status !== 'CLEARED' && pdd.status !== 'REJECTED' && pdd.pdd_admin_approved !== true;

  return (
    <>
      <style>{`
        @keyframes cfShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes cfSpin { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes cfFadeIn { from{opacity:0;transform:translateY(5px)} to{opacity:1;transform:translateY(0)} }
        .pdd-row:hover { background:#F8FAFC !important; }
        .pdd-row { transition: background 0.1s; }
      `}</style>

      <Layout pageTitle="PDD Management">
        <div style={{ display:'flex', flexDirection:'column', gap:16,
          animation:'cfFadeIn 0.2s ease both', fontFamily:"inherit" }}>

          {/* ── HEADER CARD ── */}
          <div style={{ background:'linear-gradient(120deg,#0D9488,#0F766E)', borderRadius:14,
            padding:'20px 24px', boxShadow:'0 4px 18px rgba(13,148,136,0.22)', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
              background:'linear-gradient(90deg,#F5A623,#FBBf4A,transparent)' }}/>
            <div style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.55)',
              textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>Admin Panel</div>
            <div style={{ fontSize:21, fontWeight:800, color:'#fff', letterSpacing:'-0.02em' }}>
              PDD Management
            </div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.6)', marginTop:4 }}>
              Review PDD submissions — Approve or Reject each case
            </div>
            {/* Pending count pill */}
            <div style={{ marginTop:14, display:'inline-flex', alignItems:'center', gap:6,
              padding:'5px 14px', borderRadius:20, background:'rgba(255,255,255,0.15)',
              border:'1px solid rgba(255,255,255,0.25)', fontSize:12.5, fontWeight:700, color:'#FEF3DC' }}>
              <FiClock size={13}/>
              {loading ? '…' : `${total} record${total !== 1 ? 's' : ''} ${filter !== 'ALL' ? `(${filter.toLowerCase()})` : ''}`}
            </div>
          </div>

          {/* ── FILTER + SEARCH BAR ── */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
            padding:'12px 16px', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap',
            boxShadow:'0 1px 4px rgba(13,59,94,0.05)' }}>

            {/* Status tabs */}
            <div style={{ display:'flex', gap:6 }}>
              {[
                { val:'PENDING',  label:'⏳ Pending' },
                { val:'ALL',      label:'All'        },
                { val:'CLEARED',  label:'✓ Approved' },
                { val:'REJECTED', label:'✗ Rejected' },
              ].map(tab => {
                const on = filter === tab.val;
                const tabColor = tab.val === 'PENDING' ? C.gold
                               : tab.val === 'CLEARED' ? C.green
                               : tab.val === 'REJECTED' ? C.red
                               : C.navy;
                return (
                  <button key={tab.val} onClick={() => onTabClick(tab.val)}
                    style={{ padding:'7px 14px', borderRadius:20, fontFamily:'inherit',
                      border:`1.5px solid ${on ? tabColor : C.border}`,
                      background: on ? tabColor : C.white,
                      color: on ? '#fff' : C.t2,
                      fontSize:12.5, fontWeight:700, cursor:'pointer', transition:'all 0.12s' }}>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <div style={{ flex:1, minWidth:180, position:'relative' }}>
              <FiSearch size={13} style={{ position:'absolute', left:10, top:'50%',
                transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/>
              <input type="text" placeholder="Search lead name, lead no..."
                value={search} onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if(e.key==='Enter') fetchData(1, filter, search); }}
                style={{ width:'100%', height:38, border:`1.5px solid ${C.border}`, borderRadius:9,
                  padding:'0 12px 0 30px', fontSize:13, color:C.t1, background:'#FAFBFC',
                  outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}/>
            </div>

            <button onClick={() => fetchData(page, filter, search)} title="Refresh"
              style={{ width:38, height:38, borderRadius:9, background:C.navyL,
                border:`1px solid ${C.border}`, color:C.navy, cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center' }}>
              <FiRefreshCw size={13}/>
            </button>
          </div>

          {/* ── TABLE ── */}
          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`,
            overflow:'hidden', boxShadow:'0 1px 4px rgba(13,59,94,0.05)' }}>

            {/* Loading shimmer */}
            {loading && (
              <div style={{ padding:16 }}>
                {[1,2,3,4,5].map(i => <Sh key={i}/>)}
              </div>
            )}

            {/* Empty state */}
            {!loading && records.length === 0 && (
              <div style={{ padding:'56px 24px', textAlign:'center' }}>
                <div style={{ width:54, height:54, borderRadius:'50%', background:C.tealL,
                  display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                  <FiCheckSquare size={22} style={{ color:C.teal }}/>
                </div>
                <div style={{ fontSize:15, fontWeight:700, color:C.t1, marginBottom:6 }}>
                  {filter === 'PENDING' ? 'No pending PDD approvals 🎉' : 'No records found'}
                </div>
                <div style={{ fontSize:13, color:C.t4 }}>
                  {filter === 'PENDING' ? 'All PDD cases have been reviewed.' : 'Try changing the filter above.'}
                </div>
              </div>
            )}

            {/* Table */}
            {!loading && records.length > 0 && (
              <>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', minWidth:760 }}>
                    <thead>
                      <tr style={{ background:'#F7F9FB' }}>
                        {['Lead / Customer', 'Product', 'Disbursement Amount', 'Clearance Date', 'Remarks', 'Status', 'Admin Action'].map((h, i) => (
                          <th key={h} style={{ padding:'10px 16px', fontSize:10.5, fontWeight:800,
                            color:C.t1, textTransform:'uppercase', letterSpacing:'0.07em',
                            textAlign: i === 6 ? 'center' : 'left',
                            borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {records.map(pdd => {
                        const lead = leadsMap[pdd.lead] || null;
                        const isBusy = actioning === pdd.id;
                        const pending = isPending(pdd);
                        return (
                          <tr key={pdd.id} className="pdd-row"
                            style={{ background:C.white, opacity: isBusy ? 0.6 : 1 }}>

                            {/* Lead name + no */}
                            <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.borderL}` }}>
                              <div style={{ fontWeight:700, fontSize:13.5, color:C.t1 }}>
                                {lead?.customer_name || `Lead #${pdd.lead}`}
                              </div>
                              <div style={{ fontSize:11.5, color:C.t4, marginTop:2,
                                display:'flex', alignItems:'center', gap:6 }}>
                                <span style={{ fontFamily:'monospace' }}>{lead?.lead_no || `#${pdd.lead}`}</span>
                                {lead?.mobile_number && (
                                  <><span>·</span><FiPhone size={10}/><span>{lead.mobile_number}</span></>
                                )}
                              </div>
                            </td>

                            {/* Product */}
                            <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.borderL}`,
                              fontSize:13, color:C.t2 }}>
                              {lead?.product?.name || '—'}
                            </td>

                            {/* Amount */}
                            <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.borderL}`,
                              fontSize:13, fontWeight:700, color:C.navy }}>
                              {fmtAmt(lead?.loan_amount)}
                            </td>

                            {/* Clearance date */}
                            <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.borderL}` }}>
                              <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:13, color:C.t2 }}>
                                <FiCalendar size={12} style={{ color:C.t4 }}/>
                                {fmtDate(pdd.clearance_date || pdd.created_at)}
                              </div>
                            </td>

                            {/* Remarks */}
                            <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.borderL}`,
                              fontSize:12.5, color:C.t3, maxWidth:180 }}>
                              <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                {pdd.remarks || pdd.notes || '—'}
                              </div>
                              {pdd.rejection_reason && (
                                <div style={{ fontSize:11, color:C.red, marginTop:2,
                                  display:'flex', alignItems:'center', gap:4 }}>
                                  <FiAlertCircle size={10}/> {pdd.rejection_reason}
                                </div>
                              )}
                            </td>

                            {/* Status */}
                            <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.borderL}` }}>
                              <StatusBadge pdd={pdd}/>
                            </td>

                            {/* Actions */}
                            <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.borderL}` }}>
                              {pending ? (
                                <div style={{ display:'flex', gap:8, alignItems:'center', justifyContent:'center' }}>
                                  {/* YES = Approve */}
                                  <button onClick={() => handleApprove(pdd)} disabled={isBusy}
                                    style={{ display:'flex', alignItems:'center', gap:5,
                                      height:34, padding:'0 14px', borderRadius:8,
                                      background:C.green, color:'#fff', border:'none',
                                      fontSize:12.5, fontWeight:700, cursor:isBusy?'not-allowed':'pointer',
                                      fontFamily:'inherit', minWidth:72, justifyContent:'center' }}>
                                    {isBusy ? <Spin/> : <><FiCheck size={13}/> Yes</>}
                                  </button>
                                  {/* NO = Reject */}
                                  <button onClick={() => handleReject(pdd)} disabled={isBusy}
                                    style={{ display:'flex', alignItems:'center', gap:5,
                                      height:34, padding:'0 14px', borderRadius:8,
                                      background:C.white, color:C.red,
                                      border:`1.5px solid ${C.redB}`,
                                      fontSize:12.5, fontWeight:700, cursor:isBusy?'not-allowed':'pointer',
                                      fontFamily:'inherit', minWidth:72, justifyContent:'center' }}>
                                    <FiX size={13}/> No
                                  </button>
                                </div>
                              ) : (
                                <div style={{ textAlign:'center' }}>
                                  {/* View lead link */}
                                  <button onClick={() => navigate(`/leads/${pdd.lead}?tab=revenue`)}
                                    style={{ display:'inline-flex', alignItems:'center', gap:4,
                                      padding:'5px 12px', borderRadius:8,
                                      background:C.tealL, color:C.teal,
                                      border:`1px solid ${C.teal}`,
                                      fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                                    <FiTrendingUp size={12}/> Revenue
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ borderTop:`1px solid ${C.borderL}`, padding:'12px 16px',
                    display:'flex', justifyContent:'space-between', alignItems:'center',
                    background:'#FAFBFC' }}>
                    <span style={{ fontSize:12.5, color:C.t4 }}>
                      Page {page} of {totalPages} · {total} records
                    </span>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={() => fetchData(page-1, filter, search)} disabled={page<=1}
                        style={{ padding:'6px 16px', borderRadius:8, border:`1px solid ${C.border}`,
                          background:C.white, color:page<=1?C.t4:C.navy,
                          fontSize:13, fontWeight:600, cursor:page<=1?'default':'pointer',
                          fontFamily:'inherit' }}>
                        ← Prev
                      </button>
                      <button onClick={() => fetchData(page+1, filter, search)} disabled={page>=totalPages}
                        style={{ padding:'6px 16px', borderRadius:8, border:`1px solid ${C.border}`,
                          background:C.white, color:page>=totalPages?C.t4:C.navy,
                          fontSize:13, fontWeight:600, cursor:page>=totalPages?'default':'pointer',
                          fontFamily:'inherit' }}>
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </Layout>
    </>
  );
}
